import { Router, RoutesRecognized } from '@angular/router';
import { Component, OnDestroy } from '@angular/core';
import { Platform, MenuController } from '@ionic/angular';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { AuthService, AuthState, UserType } from './shared/services/auth.service';
import { Links } from './services/util/utils';
import { Subscription } from 'rxjs';
import { pairwise, filter } from 'rxjs/operators';
import { AnalyticsService } from './services/analytics/analytics.service';
import { Browser } from '@capacitor/core';
import { AppService } from './services/app/app.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { WebsocketService } from './services/connections/websocket.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss']
})
export class AppComponent implements OnDestroy {
    public currentMenu: SideMenu[];
    readonly sellerMenu: SideMenu[] = [
        { text: 'Main Menu', url: '/seller-menu' },
        { text: 'Calculate Car Value', url: '/value' },
        { text: 'Sell My Car', url: '/form/new' },
        { text: 'See My Listings', url: '/your-listings' },
        { text: 'My Account', url: '/seller-preferences' }
    ];
    readonly dealershipMenu: SideMenu[] = [
        { text: 'Main Menu', url: '/dealership-menu' },
        { text: 'Browse Auctions', url: '/auctions' },
        { text: 'My Active Bids', url: '/bidding' },
        { text: 'Won Auctions', url: '/won' },
        { text: 'Upcoming', url: '/upcoming' },
        { text: 'Closed Auctions', url: '/closed' },
        { text: 'My Watch List', url: '/favorites' },
        { text: 'My Account', url: '/dealer-preferences' }
    ];

    env = environment;

    authSub: Subscription;

    unreadInboxMessages: number = 0;

    constructor(
        public platform: Platform,
        private splashScreen: SplashScreen,
        private statusBar: StatusBar,
        private menu: MenuController,
        public authService: AuthService,
        private router: Router,
        private translate: TranslateService,
        private app: AppService,
        private analytics: AnalyticsService,
        private ws: WebsocketService
    ) {
        this.translate.setDefaultLang('en');
        this.translate.use('en');
        this.initializeApp();

        this.router.events
            .pipe(
                filter((evt: any) => evt instanceof RoutesRecognized),
                pairwise()
            )
            .subscribe((events: RoutesRecognized[]) => {
                this.analytics.pervUrl = events[0].urlAfterRedirects;
            });
    }

    initializeApp() {
        this.platform.ready().then(async () => {
            this.statusBar.styleDefault();
            this.splashScreen.hide();
            this.analytics.initialize();
            this.app.initMinVersionCheck();
            this.app.applyStoredServer();
            if (await this.app.firstLaunch()) {
                this.router.navigateByUrl('/landing', { replaceUrl: true });
            }

            await this.app.rate();

            this.authService.authState.subscribe((auth: AuthState) => {
                if (auth) {
                    this.closeMenu();
                    this.menu.enable(true);
                    this.currentMenu = auth.type === UserType.Seller ? this.sellerMenu : this.dealershipMenu;

                    this.analytics.registerUser();
                    this.ws.init();
                } else {
                    this.menu.enable(false);
                    if (this.authService.hasPreviousLogin) {
                        this.analytics.unregisterUser();
                    }
                    this.ws.close();
                }
            });
        });

        this.platform.resume.subscribe(() => {
            this.app.initMinVersionCheck();
        });
    }

    ngOnDestroy() {
        this.authSub.unsubscribe();
    }

    public closeMenu() {
        this.menu.close('sideMenu');
    }

    public navigate(url: string, queryParams: any) {
        if (this.router.url.includes('menu')) {
            this.router.navigate([url], { replaceUrl: false, queryParams });
        } else {
            this.router.navigate([url], { replaceUrl: true });
        }
        this.closeMenu();
    }

    openTos() {
        Browser.open({ url: Links.termsOfService, windowName: 'GroovyCar' });
    }

    openPP() {
        Browser.open({ url: Links.privacyPolicy, windowName: 'GroovyCar' });
    }

    openInbox() {
        this.analytics.openInbox();
    }

    public async updateUnreadInbox() {
        if (this.platform.is('hybrid')) {
            this.unreadInboxMessages = await this.analytics.getNumberOfUnreadMessages();
        }
    }
}

export interface SideMenu {
    text: string;
    url: string;
    queryParams?: any;
}
